import React, { useCallback, useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  scanModemLookupApi,
  scanModemSaveApi,
  scanModemListApi,
  scanModemGetApi,
  scanModemRecoveredApi,
  scanModemHandoverApi,
  scanModemExportDraftApi,
  scanModemExportSessionApi,
} from "../../../services/netplusTeam";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const btn =
  "rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed";
const errMsg = (e, d) => e?.response?.data?.message || e?.message || d;

const STATUS_LABEL = {
  saved: "Saved",
  recovered: "Recovered",
  "handed-over": "Handed over to company",
};

export default function ScanModem() {
  const [code, setCode] = useState("");
  const [items, setItems] = useState([]); // draft list
  const [session, setSession] = useState(null); // saved list
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await scanModemListApi();
      setRecent(data.sessions || []);
    } catch (e) {
      /* non-fatal */
    }
  }, []);
  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const saved = !!session;
  const rows = saved ? session.items : items;

  const addCode = async (e) => {
    e.preventDefault();
    const c = code.trim();
    if (!c || saved) return;
    setBusy(true);
    try {
      const { data } = await scanModemLookupApi(c);
      const it = data.item;
      if (items.some((x) => x.macId === it.macId)) {
        Swal.fire("Already scanned", `${it.macId} is already in the list.`, "info");
      } else {
        setItems((p) => [...p, it]);
      }
      setCode("");
    } catch (err) {
      Swal.fire("Scan failed", errMsg(err, "Could not read that code."), "error");
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const download = async (call, label = "modem-scan") => {
    try {
      const res = await call();
      saveAs(res.data, `${label}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      Swal.fire("Download failed", "Could not build the Excel file.", "error");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await scanModemSaveApi(items);
      setSession(data.session);
      setItems([]);
      loadRecent();
    } catch (err) {
      Swal.fire("Save failed", errMsg(err, "Could not save the list."), "error");
    } finally {
      setBusy(false);
    }
  };

  const act = async (kind) => {
    const matched = session.items.filter((i) => i.matched).length;
    const isHand = kind === "handover";
    const ok = await Swal.fire({
      title: isHand ? "Hand over to company?" : "Mark as recovered?",
      text: isHand
        ? `${matched} modem(s) will be marked handed over to the company and moved to Modem Recovery → Hand Over.`
        : `${matched} modem(s) will be marked recovered in Modem Recovery.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isHand ? "Hand over" : "Mark recovered",
    });
    if (!ok.isConfirmed) return;
    setBusy(true);
    try {
      const call = isHand ? scanModemHandoverApi : scanModemRecoveredApi;
      const { data } = await call(session._id);
      setSession(data.session);
      loadRecent();
      Swal.fire(
        "Done",
        isHand
          ? `${data.handedOver} modem(s) handed over.`
          : `${data.recovered} modem(s) marked recovered.`,
        "success",
      );
    } catch (err) {
      Swal.fire("Failed", errMsg(err, "Action failed."), "error");
    } finally {
      setBusy(false);
    }
  };

  const openSaved = async (id) => {
    try {
      const { data } = await scanModemGetApi(id);
      setSession(data.session);
      setItems([]);
    } catch (err) {
      Swal.fire("Failed", errMsg(err, "Could not open the list."), "error");
    }
  };

  const reset = () => {
    setSession(null);
    setItems([]);
    setCode("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const unmatched = rows.filter((r) => !r.matched).length;

  return (
    <>
      <PageTitle activeMenu="Scan Modem" motherMenu="Netplus Hub" />
      <div className="space-y-4">
        <div className={`${panel} p-4`}>
          {saved ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-base font-semibold text-gray-900">
                  {session.title}
                </div>
                <div className="text-sm text-gray-500">
                  {rows.length} modem(s) · Status:{" "}
                  <b>{STATUS_LABEL[session.status] || session.status}</b>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className={`${btn} bg-emerald-600 text-white`}
                  disabled={busy || session.status !== "saved"}
                  onClick={() => act("recovered")}
                >
                  Modem Recovered
                </button>
                <button
                  className={`${btn} bg-indigo-600 text-white`}
                  disabled={busy || session.status === "handed-over"}
                  onClick={() => act("handover")}
                >
                  Modem Handover
                </button>
                <button
                  className={`${btn} border border-gray-300`}
                  onClick={() =>
                    download(() => scanModemExportSessionApi(session._id))
                  }
                >
                  Download Excel
                </button>
                <button className={`${btn} border border-gray-300`} onClick={reset}>
                  New scan
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={addCode} className="flex flex-wrap items-center gap-2">
              <input
                ref={inputRef}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Scan QR with scanner, or type / paste the MAC id, then Enter"
                className="min-w-[280px] flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-amber-500"
              />
              <button
                type="submit"
                disabled={busy || !code.trim()}
                className={`${btn} bg-amber-500 text-white`}
              >
                Add
              </button>
              <button
                type="button"
                disabled={busy || !items.length}
                className={`${btn} border border-gray-300`}
                onClick={() => download(() => scanModemExportDraftApi(items))}
              >
                Download Excel
              </button>
              <button
                type="button"
                disabled={busy || !items.length}
                className={`${btn} bg-gray-900 text-white`}
                onClick={save}
              >
                Save list ({items.length})
              </button>
            </form>
          )}
          {unmatched > 0 && (
            <div className="mt-3 text-sm text-amber-700">
              {unmatched} modem(s) are not linked to any customer (shown as
              "Unknown customer"). They stay in the sheet but are skipped on
              Recovered / Handover.
            </div>
          )}
        </div>

        <div className={`${panel} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                {["#", "MAC ID", "Customer name", "Phone", "Customer ID", "Partner", "Area", "Recovered", "Handed over", ""].map(
                  (h) => (
                    <th key={h} className="px-3 py-2">
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-gray-400">
                    No modems scanned yet.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.macId} className="border-t border-gray-100">
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2 font-mono">{r.macId}</td>
                  <td className="px-3 py-2">
                    {r.matched ? (
                      r.name || "—"
                    ) : (
                      <span className="text-amber-700">Unknown customer</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.phone || "—"}</td>
                  <td className="px-3 py-2">{r.userId || "—"}</td>
                  <td className="px-3 py-2">{r.partnerCode || "—"}</td>
                  <td className="px-3 py-2">{r.serviceArea || "—"}</td>
                  <td className="px-3 py-2">{r.recovered ? "Yes" : "No"}</td>
                  <td className="px-3 py-2">{r.handedOver ? "Yes" : "No"}</td>
                  <td className="px-3 py-2 text-right">
                    {!saved && (
                      <button
                        className="text-red-600"
                        onClick={() =>
                          setItems((p) => p.filter((x) => x.macId !== r.macId))
                        }
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {recent.length > 0 && (
          <div className={`${panel} p-4`}>
            <div className="mb-2 text-sm font-semibold text-gray-900">
              Saved scan lists
            </div>
            <div className="divide-y divide-gray-100 text-sm">
              {recent.slice(0, 15).map((s) => (
                <button
                  key={s._id}
                  onClick={() => openSaved(s._id)}
                  className="flex w-full items-center justify-between py-2 text-left hover:bg-gray-50"
                >
                  <span>
                    {s.title}{" "}
                    <span className="text-gray-400">
                      · {s.counts.total} modem(s) · {s.createdByName}
                    </span>
                  </span>
                  <span className="text-xs font-semibold text-gray-600">
                    {STATUS_LABEL[s.status] || s.status}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
